import React, { useEffect, useRef, useState } from 'react';
import { SettingsAPI } from '../services/api';

// Provider logo helpers. The logo endpoint needs the auth header, so the image is
// fetched as a Blob and shown via an object URL.

export const LOGO_ACCEPT = 'image/png,image/jpeg,image/webp';
export const LOGO_MAX_BYTES = 2 * 1024 * 1024;

// Client-side check mirroring the backend (which re-validates from magic bytes).
export const validateLogoFile = (file: File): string | null => {
  if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) return 'Logo must be a PNG, JPG or WebP image.';
  if (file.size > LOGO_MAX_BYTES) return 'Logo must be 2 MB or smaller.';
  return null;
};

/** Shows a provider's saved logo. `version` (e.g. updated_at) refetches after a change. */
export const ProviderLogo: React.FC<{ providerId: string; version?: string; size?: number; alt?: string }> = ({ providerId, version, size = 40, alt }) => {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;
    (async () => {
      const blob = await SettingsAPI.getProviderLogoBlob(providerId);
      if (!active || !blob) return;
      objectUrl = URL.createObjectURL(blob);
      setUrl(objectUrl);
    })();
    return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [providerId, version]);
  if (!url) return null;
  return <img src={url} alt={alt || 'Provider logo'} style={{ maxHeight: size, maxWidth: size * 3, objectFit: 'contain', display: 'block' }} />;
};

/** File picker with preview + validation. `onChange(null)` clears the pick. */
export const LogoPicker: React.FC<{
  file: File | null;
  onChange: (file: File | null) => void;
  disabled?: boolean;
  label?: string;
}> = ({ file, onChange, disabled, label = 'Logo (optional)' }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!file) { setPreview(null); return; }
    const u = URL.createObjectURL(file);
    setPreview(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  const pick = (f: File | undefined) => {
    if (!f) return;
    const v = validateLogoFile(f);
    setErr(v);
    onChange(v ? null : f);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="logo-picker">
      <label className="logo-picker-label">{label}</label>
      <div className="logo-picker-row">
        {preview && <img src={preview} alt="Logo preview" className="logo-picker-preview" />}
        <button type="button" className="logo-picker-btn" disabled={disabled} onClick={() => inputRef.current?.click()}>
          {file ? 'Change logo' : 'Choose logo'}
        </button>
        {file && (
          <button type="button" className="logo-picker-clear" disabled={disabled} onClick={() => { onChange(null); setErr(null); }}>
            Clear
          </button>
        )}
        <input ref={inputRef} type="file" accept={LOGO_ACCEPT} hidden onChange={(e) => pick(e.target.files?.[0])} />
      </div>
      <span className={err ? 'logo-picker-err' : 'logo-picker-hint'}>{err || 'PNG, JPG or WebP, up to 2 MB. Shown on provider-branded customer emails.'}</span>
      <style>{`
        .logo-picker { display: flex; flex-direction: column; gap: 6px; }
        .logo-picker-label { font-size: 0.8rem; font-weight: 600; color: var(--text-secondary); }
        .logo-picker-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .logo-picker-preview { max-height: 44px; max-width: 160px; object-fit: contain; border: 1px solid var(--border-primary); border-radius: 6px; padding: 4px; background: #fff; }
        .logo-picker-btn { background: var(--bg-tertiary, var(--bg-secondary)); color: var(--text-primary); border: 1px solid var(--border-primary); padding: 6px 12px; border-radius: 6px; font-size: 0.8rem; font-weight: 600; cursor: pointer; }
        .logo-picker-clear { background: none; border: none; color: var(--danger); font-size: 0.8rem; cursor: pointer; padding: 4px; }
        .logo-picker-hint { font-size: 0.72rem; color: var(--text-secondary); }
        .logo-picker-err { font-size: 0.72rem; color: var(--danger); }
      `}</style>
    </div>
  );
};
